import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { formatCurrency } from "@/lib/format";
import { Sparkles, TrendingDown, CalendarCheck2, Hash, Crown, Clock } from "lucide-react";

const STORAGE_KEY_PREFIX = "finly_trial_recap_seen_";

function StatTile({ icon: Icon, label, value, accent = "#0052FF", testId }) {
  return (
    <div
      className="rounded-xl border border-[#2A3447] bg-[#131C2E] p-4 flex flex-col gap-2"
      data-testid={testId}
    >
      <div className="flex items-center gap-2">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${accent}15` }}
        >
          <Icon className="w-4 h-4" style={{ color: accent }} />
        </div>
        <span className="text-[11px] uppercase tracking-[0.18em] text-[#94A3B8] font-medium">
          {label}
        </span>
      </div>
      <div
        className="font-display text-xl font-semibold tracking-tight font-numeric"
        style={{ color: accent }}
      >
        {value}
      </div>
    </div>
  );
}

export default function TrialRecapModal({ onUpgrade }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (checked || !user?.id) return;
    const key = `${STORAGE_KEY_PREFIX}${user.id}`;
    if (localStorage.getItem(key) === "1") {
      setChecked(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/summary/trial-recap`);
        if (cancelled) return;
        if (r.data?.show) {
          setData(r.data);
          setOpen(true);
        }
      } catch (_) {
        // silent: just don't show
      } finally {
        if (!cancelled) setChecked(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [checked, user?.id]);

  const markSeen = () => {
    if (user?.id) {
      localStorage.setItem(`${STORAGE_KEY_PREFIX}${user.id}`, "1");
    }
  };

  const handleOpenChange = (next) => {
    setOpen(next);
    if (!next) markSeen();
  };

  const handleUpgrade = () => {
    markSeen();
    setOpen(false);
    if (onUpgrade) onUpgrade();
  };

  const handleLater = () => {
    markSeen();
    setOpen(false);
  };

  if (!data) return null;

  const currency = data.currency || "MXN";
  const dayLabel = data.day_of_trial
    ? `Día ${data.day_of_trial} de ${data.trial_total_days}`
    : `Te quedan ${data.trial_days_remaining} días`;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-[520px] bg-[#131C2E] p-0 overflow-hidden"
        data-testid="trial-recap-modal"
      >
        {/* Header band */}
        <div className="bg-gradient-to-br from-[#0052FF] via-[#1E40AF] to-[#0B1437] text-white p-6">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-white/70 font-medium">
            <Clock className="w-3.5 h-3.5" />
            {dayLabel} · prueba gratis
          </div>
          <DialogHeader className="mt-3 space-y-2">
            <DialogTitle
              className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-white"
              data-testid="trial-recap-title"
            >
              Esta semana con Finly+…
            </DialogTitle>
            <DialogDescription className="text-white/80 text-sm leading-relaxed">
              Mira lo que ya lograste. Suscríbete antes de que termine la prueba
              para no perder tus datos ni tus presupuestos.
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Stats grid */}
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-3" data-testid="trial-recap-stats">
            <StatTile
              icon={Hash}
              label="Movimientos"
              value={data.transaction_count}
              accent="#0052FF"
              testId="recap-stat-count"
            />
            <StatTile
              icon={CalendarCheck2}
              label="Días activos"
              value={data.active_days}
              accent="#00E590"
              testId="recap-stat-active-days"
            />
            <StatTile
              icon={TrendingDown}
              label="Total registrado"
              value={formatCurrency(data.expense_total, currency)}
              accent="#EF4444"
              testId="recap-stat-expense"
            />
            <StatTile
              icon={Sparkles}
              label="Top categoría"
              value={
                data.top_expense_category
                  ? data.top_expense_category.name
                  : "—"
              }
              accent="#FBBF24"
              testId="recap-stat-top-category"
            />
          </div>

          {data.top_expense_category && (
            <div
              className="rounded-lg bg-[#131C2E] border border-[#2A3447] p-4 text-sm text-[#475569] leading-relaxed"
              data-testid="trial-recap-insight"
            >
              Tu mayor gasto está en{" "}
              <span className="font-semibold text-[#F1F5F9]">
                {data.top_expense_category.name}
              </span>{" "}
              con{" "}
              <span className="font-semibold text-[#EF4444] font-numeric">
                {formatCurrency(data.top_expense_category.amount, currency)}
              </span>
              . Con Premium podrás ponerle un tope y recibir alertas antes
              de excederlo.
            </div>
          )}

          {/* CTA */}
          <div className="flex flex-col sm:flex-row gap-3 pt-1">
            <Button
              onClick={handleUpgrade}
              className="flex-1 h-12 bg-[#0052FF] hover:bg-[#0040CC] text-white font-semibold rounded-xl shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all hover:-translate-y-0.5"
              data-testid="trial-recap-upgrade-btn"
            >
              <Crown className="w-4 h-4 mr-2" />
              Suscribirme ahora
            </Button>
            <Button
              variant="outline"
              onClick={handleLater}
              className="h-12 border-[#2A3447] text-[#475569] hover:bg-[#1B2638] rounded-xl"
              data-testid="trial-recap-later-btn"
            >
              Tal vez después
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
